import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Button,
  Alert,
  Stack,
} from "@mui/material";
import {
  Psychology as AiIcon,
  AutoAwesome as SparkleIcon,
  TrendingUp as FinancialIcon,
  Business as BusinessIcon,
  Warning as RiskIcon,
  Visibility as CatalystIcon,
} from "@mui/icons-material";
import AiGenerationLoader from "./AiGenerationLoader";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { isNative } from "../../../../lib/platform";
import { useIsMembershipActive } from "../../../../contexts/MembershipContext";

interface AiSummaryContentProps {
  symbol: string;
  year: number;
  quarter: number;
  aiData: any;
  aiLoading: boolean;
  aiError: any;
  generateMutation: any;
  onGenerateAi: () => void;
  isPolling?: boolean;
}

// Helper component for AI Summary sections
const SummarySection: React.FC<{
  title: string;
  icon: React.ReactNode;
  items: string[];
  color: string;
  isMobile?: boolean;
}> = ({ title, icon, items, color, isMobile = false }) => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: { xs: 3, sm: 4 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 0.75, sm: 1 },
          mb: { xs: 1.5, sm: 2 },
          pb: 1,
          borderBottom: `2px solid ${color}20`,
        }}
      >
        <Box sx={{ color: color, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          {icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "0.9375rem", sm: "1.125rem" },
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ pl: { xs: 0, sm: 1 } }}>
        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: 0.75, sm: 1 },
              mb: { xs: 1, sm: 1.5 },
            }}
          >
            <Box
              sx={{
                width: { xs: 5, sm: 6 },
                height: { xs: 5, sm: 6 },
                borderRadius: "50%",
                backgroundColor: color,
                mt: { xs: 0.75, sm: 1 },
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                fontSize: { xs: "0.8125rem", sm: "1rem" },
                color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
              }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const AiSummaryContent: React.FC<AiSummaryContentProps> = ({
  symbol,
  year,
  quarter,
  aiData,
  aiLoading,
  aiError,
  generateMutation,
  onGenerateAi,
  isPolling = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { trackFeature } = useAnalytics();
  // Session-derived (no provider needed — the Tour preview renders this
  // outside MembershipProvider). Non-members see the membership CTA upfront
  // instead of discovering the gate through a failed POST; the 403 branch
  // below stays as a safety net for a stale session claim.
  const { isMembershipActive } = useIsMembershipActive();
  const membershipRequired =
    !isMembershipActive || Boolean(generateMutation.data?.membershipRequired);

  // Flagship feature: an AI earnings summary was rendered on the Earnings tab.
  // Keyed on the quarter so it fires once per distinct report viewed.
  const hasSummary = Boolean(aiData?.exists && aiData?.summary);
  useEffect(() => {
    if (hasSummary) {
      trackFeature("earnings_report_viewed", {
        ticker: symbol,
        year,
        quarter,
      });
    }
  }, [hasSummary, symbol, year, quarter, trackFeature]);

  if (aiLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 1 }}>
        <AiIcon
          sx={{
            // Native's viewport-bounded card is much smaller — same design,
            // scaled down so heading, copy and button all fit comfortably.
            fontSize: isNative() ? 40 : 64,
            color: "#16a34a",
            mb: isNative() ? 1.5 : 3,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: isNative() ? "1.0625rem" : undefined,
            mb: isNative() ? 1 : 2,
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
          }}
        >
          AI Earnings Summary
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontSize: isNative() ? "0.875rem" : undefined,
            color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
            lineHeight: 1.6,
            maxWidth: 500,
            mx: "auto",
          }}
        >
          Generate an AI-powered analysis of this earnings call with financial
          highlights, business insights, risks, and future catalysts tailored
          for institutional investors.
        </Typography>

        <Button
          onClick={onGenerateAi}
          disabled={true}
          variant="contained"
          size="large"
          startIcon={<SparkleIcon />}
          sx={{
            background: "rgba(22, 163, 74, 0.5)",
            color: "rgba(255, 255, 255, 0.7)",
            fontWeight: 600,
            py: isNative() ? 1 : 1.5,
            px: isNative() ? 3 : 4,
            borderRadius: 2,
            textTransform: "none",
          }}
        >
          Generate AI Summary
        </Button>
      </Box>
    );
  }

  if (aiError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load AI summary: {aiError.message}
      </Alert>
    );
  }

  if (!aiData?.exists || !aiData.summary) {
    // Show loader when generation is in progress via any signal:
    // - isPending: mutation is in flight
    // - isPolling: we're waiting for a background generation to complete
    // - isGenerating: server confirmed a lock is active (e.g. after navigating back)
    if (generateMutation.isPending || isPolling || aiData?.isGenerating) {
      return <AiGenerationLoader isGenerating={true} />;
    }

    // /start-free-trial renders the RevenueCat paywall on native, so the
    // upgrade path never touches web billing there.
    const handleCtaClick = () => {
      if (membershipRequired) {
        navigate("/start-free-trial");
        return;
      }
      onGenerateAi();
    };

    // Show initial state with header and generate button
    return (
      <Box sx={{ textAlign: "center", py: 1 }}>
        <AiIcon
          sx={{
            // Native's viewport-bounded card is much smaller — same design,
            // scaled down so heading, copy and button all fit comfortably.
            fontSize: isNative() ? 40 : 64,
            color: "#16a34a",
            mb: isNative() ? 1.5 : 3,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: isNative() ? "1.0625rem" : undefined,
            mb: isNative() ? 1 : 2,
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
          }}
        >
          AI Earnings Summary
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontSize: isNative() ? "0.875rem" : undefined,
            color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
            lineHeight: 1.6,
            maxWidth: 500,
            mx: "auto",
          }}
        >
          Generate an AI-powered analysis of this earnings call with financial
          highlights, business insights, risks, and future catalysts tailored
          for institutional investors.
        </Typography>

        {generateMutation.error && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: 500, mx: "auto" }}>
            Failed to generate AI summary: {generateMutation.error.message}
          </Alert>
        )}

        {membershipRequired && (
          <Alert severity="info" sx={{ mb: 3, maxWidth: 500, mx: "auto" }}>
            AI earnings summaries are included with an Insight membership.
          </Alert>
        )}

        <Button
          onClick={handleCtaClick}
          disabled={generateMutation.isPending}
          variant="contained"
          size="large"
          startIcon={<SparkleIcon />}
          sx={{
            background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
            color: "white",
            fontWeight: 600,
            py: isNative() ? 1 : 1.5,
            px: isNative() ? 3 : 4,
            borderRadius: 2,
            textTransform: "none",
            "&:hover": {
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            },
          }}
        >
          {membershipRequired && "View membership options"}
          {!membershipRequired && "Generate AI Summary"}
        </Button>
      </Box>
    );
  }

  const summary = aiData.summary;

  return (
    <Box>
      {/* Headline */}
      <Box
        sx={{
          mb: { xs: 2.5, sm: 4 },
          textAlign: "center",
          mx: { xs: "2%", sm: "10%" },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.125rem", sm: "1.5rem" },
            mb: { xs: 1, sm: 2 },
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
            lineHeight: 1.3,
          }}
        >
          {summary.headline}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          Generated on {new Date(summary.generatedAt).toLocaleDateString()}
        </Typography>
      </Box>

      {/* Quarterly Summary */}
      {summary.quarterlySummary && (
        <Box
          sx={{
            mb: { xs: 3, sm: 4 },
            mx: "auto",
            maxWidth: 1050,
            textAlign: "center",
          }}
        >
          <Card
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)"
                  : "linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(34, 197, 94, 0.2)"
                  : "1px solid rgba(34, 197, 94, 0.1)",
              borderRadius: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.75, sm: 1 },
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                <SparkleIcon
                  sx={{
                    color: "#22c55e",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color:
                      theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  }}
                >
                  Quarterly Overview
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "0.8125rem", sm: "1rem" },
                  lineHeight: 1.6,
                  color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                  fontWeight: 500,
                  whiteSpace: "pre-wrap",
                }}
              >
                {summary.quarterlySummary}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Side by Side Stack for Financial & Business Highlights */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, sm: 4 }}
        sx={{ mb: { xs: 2, sm: 4 } }}
      >
        <Box sx={{ flex: 1 }}>
          <SummarySection
            title="Financial Highlights"
            icon={<FinancialIcon />}
            items={summary.financialHighlights}
            color="#22c55e"
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SummarySection
            title="Business Highlights"
            icon={<BusinessIcon />}
            items={summary.businessHighlights}
            color="#3b82f6"
            isMobile={isMobile}
          />
        </Box>
      </Stack>

      {/* Side by Side Stack for Risks & Future Catalysts */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, sm: 4 }}
        sx={{ mb: { xs: 2, sm: 4 } }}
      >
        <Box sx={{ flex: 1 }}>
          <SummarySection
            title="Risks & Concerns"
            icon={<RiskIcon />}
            items={summary.risksAndConcerns}
            color="#ef4444"
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SummarySection
            title="Future Catalysts"
            icon={<CatalystIcon />}
            items={summary.futureCatalysts}
            color="#f59e0b"
            isMobile={isMobile}
          />
        </Box>
      </Stack>

      {/* Investment Conclusion */}
      {summary.conclusion && (
        <Box
          sx={{
            mt: { xs: 3, sm: 5 },
            mb: { xs: 2, sm: 4 },
            mx: "auto",
            maxWidth: 900,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.75, sm: 1 },
              mb: { xs: 2, sm: 3 },
              pb: { xs: 1.5, sm: 2 },
              borderBottom: `2px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
            }}
          >
            <SparkleIcon
              sx={{
                color: "#6366f1",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Investment Conclusion
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.8125rem", sm: "1rem" },
              lineHeight: 1.8,
              color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
              fontWeight: 400,
              whiteSpace: "pre-wrap",
              textAlign: "left",
              px: { xs: 0, sm: 2 },
            }}
          >
            {summary.conclusion}
          </Typography>
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              pt: { xs: 1.5, sm: 2 },
              borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                fontStyle: "italic",
                lineHeight: 1.6,
                textAlign: "center",
                display: "block",
                maxWidth: 700,
                mx: "auto",
              }}
            >
              This analysis is AI-generated and should not be considered as
              investment advice. While AI models strive for accuracy, they may
              occasionally produce errors, misinterpret data, or omit critical
              context. Investors should independently verify all information and
              conduct thorough due diligence before making investment decisions.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AiSummaryContent;
